import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, CreditCard, Users, Database, Zap, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  getTailoringMode,
  setTailoringMode,
  TailoringMode,
  TAILORING_ROUTES,
  TAILORING_LABELS,
} from "@/services/appSettingsService";
import { useTailoringRoute } from "@/context/TailoringModeContext";

const MODES: { value: TailoringMode; label: string; description: string }[] = [
  {
    value: "claude_direct",
    label: "Claude Direct",
    description: "9-prompt pipeline — P0–P9 — full career summary, highlights, experience, skills. Your main production pipeline.",
  },
  {
    value: "fixed_duplicate",
    label: "Fixed Duplicate",
    description: "Original tailoring flow. Stable fallback if Claude Direct is having issues.",
  },
  {
    value: "simple",
    label: "Simple Tailoring",
    description: "Lightweight single-prompt flow. Fastest, least customization.",
  },
];

export default function AdminSettings() {
  const { toast } = useToast();
  const { mode: liveMode, setMode } = useTailoringRoute();
  const [selected, setSelected] = useState<TailoringMode>(liveMode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(liveMode);
  }, [liveMode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setMode(selected);
      toast({ title: "Saved", description: `All users will now see: ${TAILORING_LABELS[selected]}` });
    } catch {
      toast({ title: "Error", description: "Failed to save. Make sure the app_settings table exists in Supabase.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">System configuration and management</p>
      </div>

      {/* TAILORING MODE TOGGLE */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" />
            Active Tailoring Engine
          </CardTitle>
          <CardDescription>
            Controls which tailoring page ALL users land on when they click "Resume Tailoring" anywhere in the app.
            Change takes effect immediately — no redeploy needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => setSelected(m.value)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selected === m.value
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selected === m.value ? "border-primary bg-primary" : "border-gray-300"
                }`}>
                  {selected === m.value && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{m.label}</span>
                    {liveMode === m.value && (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <CheckCircle2 className="w-3 h-3" /> LIVE
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{m.description}</p>
                  <p className="text-xs text-gray-400 mt-1 font-mono">{TAILORING_ROUTES[m.value]}</p>
                </div>
              </div>
            </button>
          ))}

          <Button
            onClick={handleSave}
            disabled={saving || selected === liveMode}
            className="w-full mt-2"
          >
            {saving ? "Saving..." : selected === liveMode ? "Already Active" : `Switch to ${TAILORING_LABELS[selected]}`}
          </Button>

          {selected !== liveMode && (
            <p className="text-xs text-amber-600 text-center">
              Unsaved — click the button above to apply this change for all users.
            </p>
          )}
        </CardContent>
      </Card>

      {/* SQL SETUP NOTE */}
      <Card className="border border-amber-200 bg-amber-50">
        <CardContent className="pt-4">
          <p className="text-sm text-amber-800 font-medium mb-1">First-time setup required</p>
          <p className="text-xs text-amber-700">
            Run the SQL in{" "}
            <code className="bg-amber-100 px-1 rounded">supabase/migrations/20260713_create_app_settings.sql</code>{" "}
            in your Supabase dashboard → SQL Editor if the toggle isn't saving.
          </p>
        </CardContent>
      </Card>

      {/* EXISTING SETTINGS CARDS */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Credit System
            </CardTitle>
            <CardDescription>Manage subscription plans and credit costs</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Configure pricing, credit allocations, and action costs for the platform.
            </p>
            <Link to="/admin/billing">
              <Button variant="outline" className="w-full">Manage Plans & Costs</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Management
            </CardTitle>
            <CardDescription>View and manage user accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Search users, modify credits, change plans, or delete accounts.
            </p>
            <Link to="/admin/users">
              <Button variant="outline" className="w-full">Manage Users</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Pipeline Management
            </CardTitle>
            <CardDescription>Configure VectorShift pipelines</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Manage AI pipeline configurations and prompts.
            </p>
            <Link to="/admin/pipelines">
              <Button variant="outline" className="w-full">Manage Pipelines</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              System Info
            </CardTitle>
            <CardDescription>Platform configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Platform</span>
                <span>TailorWiz</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Backend</span>
                <span>Supabase</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Engine</span>
                <span className="font-medium">{TAILORING_LABELS[liveMode]}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
